import {
	Button,
	Checkbox,
	Link,
	Search,
	Select,
	SpinnerWithBackground,
	TabBar,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	ZipIcon,
} from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { WorkflowsTab } from '../WorkflowsTab'
import { CsvIcon, type ExportMenuItem, ExportSplitButton } from './components/ExportSplitButton'
import { GlobalPerformanceSection } from './components/GlobalPerformanceSection'
import { ParticipantProfileModal } from './components/ParticipantProfileModal'
import { DISTRIBUTION_TIERS, DistributionBar, formatDealsInvolved, getRatingColor } from './components/shared'
import { useRatingScaleMax } from './components/useRatingScaleMax'
import { useDealTeamAccess } from './hooks/useDealTeamAccess'
import type { AggregationStore } from './stores/aggregationStore'
import { useAggregationStore } from './stores/aggregationStore'

type TopTab = 'dashboard' | 'data-explorer' | 'workflows'

export function AggregationPortalPage() {
	const organizationId = sessionStore((s) => s.user?.organizationId)
	const navigate = useNavigate()
	const { isAuthorized, isLoading: accessLoading } = useDealTeamAccess()
	const {
		summary,
		isLoading,
		error,
		errorType,
		operationError,
		initialize,
		setOperationError,
		selectParticipant,
		setActiveTab,
	} = useAggregationStore()

	const [searchParams, setSearchParams] = useSearchParams()
	const topTab: TopTab = (searchParams.get('tab') as TopTab) ?? 'dashboard'
	const setTopTab = (tab: TopTab) => setSearchParams({ tab }, { replace: true })

	useEffect(() => {
		if (organizationId) {
			initialize(organizationId)
		}
	}, [organizationId, initialize])

	// After init, honour a participantId deep-link (e.g. opened from "View Profile" in a new tab)
	useEffect(() => {
		const participantId = searchParams.get('participantId')
		if (participantId && summary) {
			setActiveTab('participants')
			selectParticipant(participantId)
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev)
					next.delete('participantId')
					return next
				},
				{ replace: true },
			)
		}
	}, [summary, searchParams, setActiveTab, selectParticipant, setSearchParams])

	if (accessLoading || isLoading) {
		return <SpinnerWithBackground />
	}

	if (!isAuthorized) {
		return (
			<main className='flex flex-col items-center justify-center p-8 gap-4'>
				<Text variant='large' weight='semibold'>
					Access Denied
				</Text>
				<Text variant='small' className='text-neutral-500'>
					You don't have permission to access the Deal Team Aggregation Portal.
				</Text>
				<Button variant='secondary' onClick={() => navigate('/dashboard')}>
					Back to Dashboard
				</Button>
			</main>
		)
	}

	if (error) {
		if (errorType === 'forbidden') {
			return (
				<main className='flex flex-col items-center justify-center p-8 gap-4'>
					<Text variant='large' weight='semibold'>
						Access Denied
					</Text>
					<Text variant='small' className='text-neutral-500'>
						You don't have permission to access the Deal Team Aggregation Portal.
					</Text>
					<Button variant='secondary' onClick={() => navigate('/dashboard')}>
						Back to Dashboard
					</Button>
				</main>
			)
		}
		return (
			<main className='flex flex-col items-center justify-center p-8 gap-4'>
				<Text variant='large' weight='semibold' className='text-red-600'>
					Something went wrong
				</Text>
				<Text variant='small' className='text-neutral-500'>
					{error}
				</Text>
				<Button variant='secondary' onClick={() => organizationId && initialize(organizationId)}>
					Retry
				</Button>
			</main>
		)
	}

	return (
		<main className='flex flex-col gap-6 p-6'>
			<div className='flex items-start justify-between gap-4'>
				<div className='flex flex-col gap-1 text-left'>
					<div className='flex items-center'>
						<Link href='/dashboard' variant='breadcrumb' size='small' withChevron>
							Dashboard
						</Link>
						<Link variant='breadcrumb' size='small' withChevron disabled>
							DealTeam
						</Link>
						<Link variant='breadcrumb' size='small' disabled>
							{topTab === 'workflows' ? 'Workflows' : 'Aggregation Portal'}
						</Link>
					</div>
					<Text variant='h3' weight='semibold' className='text-left'>
						{topTab === 'workflows' ? 'Workflows' : 'Aggregation Portal'}
					</Text>
				</div>
				{/* TODO: implement archive */}
			</div>

			{operationError && (
				<div className='flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3'>
					<Text variant='small' className='text-red-700'>
						{operationError}
					</Text>
					<button
						type='button'
						className='ml-4 text-red-500 hover:text-red-700 text-sm font-medium'
						onClick={() => setOperationError(null)}>
						Dismiss
					</button>
				</div>
			)}

			<TabBar
				tabs={[
					{ id: 'dashboard', label: 'Dashboard' },
					{ id: 'data-explorer', label: 'Data Explorer' },
					{ id: 'workflows', label: 'Workflows' },
				]}
				activeTab={topTab}
				onChange={(tab) => setTopTab(tab as TopTab)}
				aria-label='Aggregation portal sections'
			/>

			{topTab === 'dashboard' && <DashboardTab summary={summary} />}
			{topTab === 'data-explorer' && <DataExplorerTab />}
			{topTab === 'workflows' && organizationId && <WorkflowsTab organizationId={organizationId} />}
		</main>
	)
}

type TrendProps = { value: string; direction: 'up' | 'down' | 'stable'; tooltip?: string }

function TrendPill({ trend }: { trend: TrendProps | null }) {
	if (trend === null) return null

	const pillClass =
		trend.direction === 'up'
			? 'bg-emerald-100 text-emerald-700'
			: trend.direction === 'down'
				? 'bg-red-100 text-red-600'
				: 'bg-neutral-100 text-neutral-600'
	const icon = trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : null

	const pill = (
		<span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${pillClass}`}>
			{icon && <span>{icon}</span>}
			{trend.value}
		</span>
	)

	if (!trend.tooltip) return pill

	return (
		<Tooltip>
			<TooltipTrigger asChild>{pill}</TooltipTrigger>
			<TooltipContent>{trend.tooltip}</TooltipContent>
		</Tooltip>
	)
}

function MetricCard({ label, value, trend }: { label: string; value: number | string; trend?: TrendProps | null }) {
	return (
		<div className='rounded-lg border border-neutral-200 bg-white px-5 py-4 shadow-sm text-left'>
			<Text variant='xsmall' className='mb-2 text-neutral-500'>
				{label}
			</Text>
			<div className='flex items-center gap-3'>
				<Text variant='h3'>{value}</Text>
				{trend !== undefined && <TrendPill trend={trend} />}
			</div>
		</div>
	)
}

function DashboardTab({ summary }: { summary: AggregationStore['summary'] }) {
	if (!summary) return null

	const completionRate =
		summary.totalEvaluations > 0 ? Math.round((summary.completedEvaluations / summary.totalEvaluations) * 100) : 0

	return (
		<div className='flex flex-col gap-6'>
			<div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
				<MetricCard
					label='Total Deals'
					value={summary.totalCycles}
					trend={
						summary.totalCyclesTrend != null
							? {
									// Arrow already conveys direction — show the magnitude only.
									value: `${Math.abs(summary.totalCyclesTrend)}%`,
									direction: summary.totalCyclesTrend > 0 ? 'up' : summary.totalCyclesTrend < 0 ? 'down' : 'stable',
									tooltip:
										summary.totalCyclesTrend > 0
											? 'Increase vs. Previous Year'
											: summary.totalCyclesTrend < 0
												? 'Decrease vs. Previous Year'
												: 'No change vs. Previous Year',
								}
							: null
					}
				/>
				<MetricCard
					label='Performance'
					value={summary.overallRating != null ? summary.overallRating.toFixed(2) : '—'}
					trend={
						summary.avgRatingTrend != null
							? {
									// Express as a percentage of the 10-point rating scale so the pill
									// reads "↓ 0.5%" rather than "↓ -0.05" — matches the Figma format.
									value: `${Math.abs(Math.round(summary.avgRatingTrend * 10) / 10)}%`,
									direction: summary.avgRatingTrend > 0 ? 'up' : summary.avgRatingTrend < 0 ? 'down' : 'stable',
									tooltip: 'vs. Previous Year',
								}
							: null
					}
				/>
				<MetricCard
					label='Completion Rate'
					value={`${completionRate}%`}
					trend={
						summary.completionRateTrend != null
							? {
									value:
										summary.completionRateTrend === 'stable'
											? 'Stable'
											: summary.completionRateTrend === 'up'
												? 'Improving'
												: 'Declining',
									direction: summary.completionRateTrend,
									tooltip: 'vs. Previous Year',
								}
							: null
					}
				/>
				<MetricCard label='Active Participants' value={summary.totalParticipants} />
			</div>

			<GlobalPerformanceSection trend={summary.performanceTrend} />
			<CycleInsightsSection />
		</div>
	)
}

function CycleInsightsSection() {
	const { cycleTemplates, selectedTemplateId, cycleInsights, isCycleInsightsLoading, selectTemplate } =
		useAggregationStore()
	const scaleMax = useRatingScaleMax()

	const selectedTemplate = cycleTemplates.find((t) => t.templateId === selectedTemplateId)

	if (cycleTemplates.length === 0) {
		return (
			<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-6 text-left'>
				<Text variant='large' weight='semibold'>
					Cycle Insights
				</Text>
				<div className='flex items-center justify-center py-12 text-neutral-400'>
					<Text variant='small'>No deal templates found for this organization.</Text>
				</div>
			</div>
		)
	}

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-6 text-left'>
			<div className='flex items-center justify-between gap-4'>
				<Text variant='large' weight='semibold'>
					Cycle Insights
				</Text>
				<div className='flex items-center gap-2'>
					<span className='text-sm text-neutral-500'>Cycle Template</span>
					<div className='w-56'>
						<Select
							sizing='small'
							options={cycleTemplates.map((t) => ({
								value: t.templateId,
								label: `${t.templateName} (${t.cycleCount} ${t.cycleCount === 1 ? 'Deal' : 'Deals'})`,
							}))}
							value={selectedTemplateId ?? ''}
							onChange={(v: string) => selectTemplate(v)}
						/>
					</div>
				</div>
			</div>

			<div className='rounded-lg border border-neutral-200 p-6 flex flex-col gap-6 text-left'>
				{selectedTemplate && (
					<Text variant='large' weight='semibold'>
						{selectedTemplate.templateName}
					</Text>
				)}

				<div className='flex flex-wrap gap-x-6 gap-y-2'>
					{DISTRIBUTION_TIERS.map((tier) => (
						<div key={tier.key} className='flex items-center gap-1.5'>
							<div className='h-3 w-3 rounded-full shrink-0' style={{ backgroundColor: tier.color }} />
							<span className='text-xs text-neutral-600'>{tier.label}</span>
						</div>
					))}
				</div>

				{!isCycleInsightsLoading && cycleInsights?.questions.length === 0 && (
					<div className='flex items-center justify-center py-10 text-neutral-400'>
						<Text variant='small'>No rating questions found for this template.</Text>
					</div>
				)}

				{cycleInsights && cycleInsights.questions.length > 0 && (
					<div
						className={`rounded-lg border border-neutral-200 flex flex-col divide-y divide-neutral-100 transition-opacity duration-150 ${isCycleInsightsLoading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
						{cycleInsights.questions.map((q) => (
							<div key={q.questionId} className='px-6 py-5 flex flex-col gap-3'>
								<p className='text-base font-normal text-neutral-900'>{q.questionLabel}</p>
								<div className='flex items-center gap-6'>
									<div className='flex flex-col gap-1 flex-1'>
										<span className='text-xs text-neutral-400'>Rating Distribution</span>
										<DistributionBar distribution={q.distribution} />
									</div>
									<div className='flex gap-4 shrink-0'>
										{[
											{ label: 'Overall Rating', value: q.overallRating },
											{ label: 'Highest Rating', value: q.highestRating },
											{ label: 'Lowest Rating', value: q.lowestRating },
										].map(({ label, value }) => (
											<div key={label} className='flex flex-col items-center gap-1'>
												<span className='text-xs text-neutral-500'>{label}</span>
												{value === null ? (
													<span className='text-xs text-neutral-400'>--</span>
												) : (
													<span
														className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(value, scaleMax)}`}>
														{value.toFixed(2)}
													</span>
												)}
											</div>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				)}

				{isCycleInsightsLoading && !cycleInsights && (
					<div className='flex items-center justify-center py-10 text-neutral-400'>
						<Text variant='small'>Loading...</Text>
					</div>
				)}
			</div>
		</div>
	)
}

function DataExplorerTab() {
	const { activeTab, setActiveTab, selectedParticipantId } = useAggregationStore()

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center justify-between'>
				<Text variant='large' weight='semibold'>
					{activeTab === 'deals' ? 'Deals History' : 'Participants Ratings'}
				</Text>
				<div className='flex -space-x-px'>
					<button
						type='button'
						className={`px-4 py-1.5 text-sm font-medium rounded-l-lg border transition-colors ${
							activeTab === 'deals'
								? 'z-10 border-neutral-500 bg-neutral-50 text-neutral-800'
								: 'border-neutral-300 bg-white text-neutral-500 hover:bg-neutral-50'
						}`}
						onClick={() => setActiveTab('deals')}>
						Deals
					</button>
					<button
						type='button'
						className={`px-4 py-1.5 text-sm font-medium rounded-r-lg border transition-colors ${
							activeTab === 'participants'
								? 'z-10 border-neutral-500 bg-neutral-50 text-neutral-800'
								: 'border-neutral-300 bg-white text-neutral-500 hover:bg-neutral-50'
						}`}
						onClick={() => setActiveTab('participants')}>
						Participants
					</button>
				</div>
			</div>

			{activeTab === 'deals' && <DealsTab />}
			{activeTab === 'participants' && <ParticipantsTab />}

			{selectedParticipantId && <ParticipantProfileModal />}
		</div>
	)
}

function DealsTab() {
	const navigate = useNavigate()
	const { cycles, cycleSearchQuery, setCycleSearchQuery, exportCycles, isExporting } = useAggregationStore()
	const scaleMax = useRatingScaleMax()
	const [searchInput, setSearchInput] = useState(cycleSearchQuery.search ?? '')
	const [filtersOpen, setFiltersOpen] = useState(false)
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const filtersRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
				setFiltersOpen(false)
			}
		}
		if (filtersOpen) document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [filtersOpen])

	// biome-ignore lint/correctness/useExhaustiveDependencies: cycleSearchQuery is used as a trigger to reset selection, not inside the callback
	useEffect(() => {
		setSelectedIds(new Set())
	}, [cycleSearchQuery])

	const handleSearch = (value: string) => {
		setSearchInput(value)
		setCycleSearchQuery({ search: value || undefined, page: 1 })
	}

	const handleSort = (col: 'dealName' | 'closeDate' | 'createdAt' | 'overallRating') => {
		const current = cycleSearchQuery.sortBy ?? 'createdAt'
		const currentOrder = cycleSearchQuery.order ?? 'desc'
		const defaultDesc = col === 'overallRating' || col === 'closeDate'
		const toggledOrder = currentOrder === 'asc' ? 'desc' : 'asc'
		const defaultOrder = defaultDesc ? 'desc' : 'asc'
		const nextOrder = current === col ? toggledOrder : defaultOrder
		setCycleSearchQuery({ sortBy: col, order: nextOrder, page: 1 })
	}

	const sortIcon = (col: string) => {
		const active = (cycleSearchQuery.sortBy ?? 'createdAt') === col
		const asc = (cycleSearchQuery.order ?? 'desc') === 'asc'
		const directionIcon = asc ? '▲' : '▼'
		const icon = active ? directionIcon : '⇅'
		return (
			<span className={`ml-1 inline-block text-[10px] ${active ? 'text-neutral-700' : 'text-neutral-300'}`}>
				{icon}
			</span>
		)
	}

	const handleStatusChange = (value: string) => {
		const status = value as NonNullable<AggregationStore['cycleSearchQuery']['status']> | ''
		setCycleSearchQuery({ status: status || undefined, page: 1 })
	}

	const handleSourceChange = (value: string) => {
		const source = value as NonNullable<AggregationStore['cycleSearchQuery']['source']> | ''
		setCycleSearchQuery({ source: source || undefined, page: 1 })
	}

	const handleCloseDateFromChange = (value: string) => {
		setCycleSearchQuery({ closeDateFrom: value || undefined, page: 1 })
	}

	const handleCloseDateToChange = (value: string) => {
		setCycleSearchQuery({ closeDateTo: value || undefined, page: 1 })
	}

	const activeFilterCount = [
		cycleSearchQuery.closeDateFrom,
		cycleSearchQuery.closeDateTo,
		cycleSearchQuery.status,
		cycleSearchQuery.source,
	].filter(Boolean).length

	const rows = cycles?.data ?? []
	const allSelected = rows.length > 0 && rows.every((c) => selectedIds.has(c.cycleId))
	const someSelected = selectedIds.size > 0 && !allSelected
	let selectAllState: boolean | undefined
	if (allSelected) selectAllState = true
	else if (someSelected) selectAllState = false

	const toggleAll = () => {
		if (allSelected) {
			setSelectedIds(new Set())
		} else {
			setSelectedIds(new Set(rows.map((c) => c.cycleId)))
		}
	}

	const toggleRow = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const dealsExportItems: ExportMenuItem[] = [
		{
			key: 'csv-selected',
			label:
				selectedIds.size > 0 ? (
					<>
						Export selected <strong>({selectedIds.size})</strong> deals (.csv)
					</>
				) : (
					'Export selected deals (.csv)'
				),
			ariaLabel:
				selectedIds.size > 0 ? `Export selected (${selectedIds.size}) deals (.csv)` : 'Export selected deals (.csv)',
			icon: <CsvIcon className='text-neutral-500' />,
			action: () => exportCycles([...selectedIds]),
			busy: isExporting,
			disabled: selectedIds.size === 0,
		},
		{
			key: 'csv-all',
			label: 'Export all deals (.csv)',
			ariaLabel: 'Export all deals (.csv)',
			icon: <CsvIcon className='text-neutral-500' />,
			action: () => exportCycles(),
			busy: isExporting,
		},
	]

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center gap-3'>
				<div className='flex-1'>
					<Search value={searchInput} onChange={(e) => handleSearch(e.target.value)} placeholder='Search' />
				</div>

				<div ref={filtersRef} className='relative'>
					<button
						type='button'
						onClick={() => setFiltersOpen((v) => !v)}
						className='flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50'>
						<svg
							width='16'
							height='16'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							role='img'
							aria-label='Filters'>
							<line x1='4' y1='6' x2='20' y2='6' />
							<line x1='8' y1='12' x2='16' y2='12' />
							<line x1='11' y1='18' x2='13' y2='18' />
						</svg>
						Filters
						{activeFilterCount > 0 && (
							<span className='flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white'>
								{activeFilterCount}
							</span>
						)}
					</button>

					{filtersOpen && (
						<div className='absolute right-0 top-full z-10 mt-1 w-80 rounded-lg border border-neutral-200 bg-white p-4 shadow-lg'>
							<div className='flex flex-col gap-4'>
								<div className='flex flex-col gap-1'>
									<label htmlFor='filter-date-from' className='text-xs font-medium text-neutral-600'>
										Close date from
									</label>
									<input
										id='filter-date-from'
										type='date'
										value={cycleSearchQuery.closeDateFrom ?? ''}
										onChange={(e) => handleCloseDateFromChange(e.target.value)}
										className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
									/>
								</div>
								<div className='flex flex-col gap-1'>
									<label htmlFor='filter-date-to' className='text-xs font-medium text-neutral-600'>
										Close date to
									</label>
									<input
										id='filter-date-to'
										type='date'
										value={cycleSearchQuery.closeDateTo ?? ''}
										onChange={(e) => handleCloseDateToChange(e.target.value)}
										className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
									/>
								</div>
								<div className='flex flex-col gap-1'>
									<label htmlFor='filter-status' className='text-xs font-medium text-neutral-600'>
										Status
									</label>
									<select
										id='filter-status'
										value={cycleSearchQuery.status ?? ''}
										onChange={(e) => handleStatusChange(e.target.value)}
										className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'>
										<option value=''>All Statuses</option>
										<option value='in-progress'>In Progress</option>
										<option value='completed'>Completed</option>
										<option value='draft'>Draft</option>
										<option value='ready-for-launch'>Ready for Launch</option>
									</select>
								</div>
								<div className='flex flex-col gap-1'>
									<label htmlFor='filter-source' className='text-xs font-medium text-neutral-600'>
										Source
									</label>
									<select
										id='filter-source'
										value={cycleSearchQuery.source ?? ''}
										onChange={(e) => handleSourceChange(e.target.value)}
										className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'>
										<option value=''>All Sources</option>
										<option value='crm'>CRM</option>
										<option value='manual_form'>Manual Form</option>
										<option value='manual_csv'>Manual CSV</option>
										<option value='webhook'>Webhook</option>
									</select>
								</div>
								{activeFilterCount > 0 && (
									<button
										type='button'
										onClick={() => {
											setCycleSearchQuery({
												closeDateFrom: undefined,
												closeDateTo: undefined,
												status: undefined,
												source: undefined,
												page: 1,
											})
										}}
										className='text-xs text-blue-600 hover:underline text-left'>
										Clear all filters
									</button>
								)}
							</div>
						</div>
					)}
				</div>

				<ExportSplitButton items={dealsExportItems} />
			</div>

			<div>
				<div className='overflow-x-auto rounded-t-lg border border-neutral-200'>
					<table className='w-full text-sm'>
						<thead>
							<tr>
								<th className='w-10 px-6 py-3 text-left'>
									<Checkbox
										size='small'
										checked={allSelected || someSelected}
										isAllSelected={selectAllState}
										onChange={toggleAll}
									/>
								</th>
								<th
									className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer hover:text-neutral-700 select-none'
									onClick={() => handleSort('dealName')}>
									Deal Name{sortIcon('dealName')}
								</th>
								<th
									className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer hover:text-neutral-700 select-none'
									onClick={() => handleSort('closeDate')}>
									Close Date{sortIcon('closeDate')}
								</th>
								<th className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap'>Participants</th>
								<th
									className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer hover:text-neutral-700 select-none'
									onClick={() => handleSort('overallRating')}>
									Overall Rating{sortIcon('overallRating')}
								</th>
								<th className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap'>
									Highest Rated Participant
								</th>
								<th className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap'>Actions</th>
							</tr>
						</thead>
						<tbody className='divide-y divide-neutral-100'>
							{rows.length === 0 && (
								<tr>
									<td colSpan={7} className='px-6 py-8 text-center text-neutral-500'>
										No deal team cycles found
									</td>
								</tr>
							)}
							{rows.map((cycle) => (
								<tr key={cycle.cycleId} className='hover:bg-neutral-50'>
									<td className='px-6 py-4 text-left'>
										<Checkbox
											size='small'
											checked={selectedIds.has(cycle.cycleId)}
											onChange={() => toggleRow(cycle.cycleId)}
										/>
									</td>
									<td className='px-6 py-4 text-left font-medium'>{cycle.dealName || cycle.cycleName}</td>
									<td className='px-6 py-4 text-left text-neutral-500'>{formatOptionalDate(cycle.dealClosedDate)}</td>
									<td className='px-6 py-4 text-left'>
										{cycle.completedEvaluations}/{cycle.participantCount}
									</td>
									<td className='px-6 py-4 text-left'>
										{cycle.overallRating != null ? (
											<span
												className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(Number(cycle.overallRating), scaleMax)}`}>
												{Number(cycle.overallRating).toFixed(2)}
											</span>
										) : (
											<span className='text-neutral-400'>{'\u2014'}</span>
										)}
									</td>
									<td className='px-6 py-4 text-left'>
										{cycle.highestRatedParticipantName && cycle.highestRatedParticipantScore != null ? (
											<span className='flex items-center gap-1.5'>
												<span
													className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(Number(cycle.highestRatedParticipantScore), scaleMax)}`}>
													{Number(cycle.highestRatedParticipantScore).toFixed(2)}
												</span>
												{cycle.highestRatedParticipantName}
											</span>
										) : (
											<span className='text-neutral-400'>{'\u2014'}</span>
										)}
									</td>
									<td className='px-6 py-4 text-left'>
										<Button
											variant='secondary'
											size='small'
											onClick={() => navigate(`/dealteam/deals/${cycle.cycleId}`)}>
											View Details
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{cycles?.meta && <Pagination meta={cycles.meta} onPageChange={(p) => setCycleSearchQuery({ page: p })} />}
			</div>
		</div>
	)
}

function ParticipantsTab() {
	const {
		participants,
		participantSearchQuery,
		setParticipantSearchQuery,
		searchParticipants,
		selectParticipant,
		exportDealTeamParticipantsCsv,
		exportDealTeamParticipantDealsCsv,
		exportDealTeamParticipantsReportsZip,
		isExportingDealTeamParticipantsCsv,
		isExportingDealTeamParticipantDealsCsv,
		isExportingDealTeamParticipantsReportsZip,
	} = useAggregationStore()
	const scaleMax = useRatingScaleMax()
	const [searchInput, setSearchInput] = useState(participantSearchQuery.search ?? '')
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
	useEffect(() => {
		searchParticipants()
	}, [searchParticipants])

	const rows = participants?.data ?? []

	useEffect(() => {
		setSelectedIds((prev) => {
			if (prev.size === 0) return prev
			const valid = new Set(rows.map((r) => r.userId))
			let changed = false
			const next = new Set<string>()
			for (const id of prev) {
				if (valid.has(id)) next.add(id)
				else changed = true
			}
			return changed ? next : prev
		})
	}, [rows])

	const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.userId))
	const someSelected = selectedIds.size > 0 && !allSelected
	let selectAllState: boolean | undefined
	if (allSelected) selectAllState = true
	else if (someSelected) selectAllState = false

	const toggleAll = () => {
		if (allSelected) setSelectedIds(new Set())
		else setSelectedIds(new Set(rows.map((r) => r.userId)))
	}

	const toggleRow = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const hasSelection = selectedIds.size > 0
	const selectionCount = selectedIds.size
	const selectionScopeLabel = hasSelection ? (
		<>
			selected <strong>({selectionCount})</strong> participants
		</>
	) : (
		'all participants'
	)
	const selectedIdsArray = hasSelection ? [...selectedIds] : undefined

	const participantsExportItems: ExportMenuItem[] = [
		{
			key: 'profiles-csv',
			label: <>Export {selectionScopeLabel} profiles (.csv)</>,
			ariaLabel: hasSelection
				? `Export selected (${selectionCount}) participants profiles (.csv)`
				: 'Export all participants profiles (.csv)',
			icon: <CsvIcon className='text-neutral-500' />,
			action: () => exportDealTeamParticipantsCsv(selectedIdsArray),
			busy: isExportingDealTeamParticipantsCsv,
		},
		{
			key: 'deals-csv',
			label: <>Export {selectionScopeLabel} deals (.csv)</>,
			ariaLabel: hasSelection
				? `Export selected (${selectionCount}) participants deals (.csv)`
				: 'Export all participants deals (.csv)',
			icon: <CsvIcon className='text-neutral-500' />,
			action: () => exportDealTeamParticipantDealsCsv(selectedIdsArray),
			busy: isExportingDealTeamParticipantDealsCsv,
		},
		{
			key: 'reports-zip',
			label: <>Export {selectionScopeLabel} reports (.zip)</>,
			ariaLabel: hasSelection
				? `Export selected (${selectionCount}) participants reports (.zip)`
				: 'Export all participants reports (.zip)',
			icon: <ZipIcon size={20} className='fill-neutral-500' />,
			action: () => exportDealTeamParticipantsReportsZip({ userIds: selectedIdsArray }),
			busy: isExportingDealTeamParticipantsReportsZip,
		},
	]

	const handleSearch = (value: string) => {
		setSearchInput(value)
		setParticipantSearchQuery({ search: value || undefined, page: 1 })
	}

	const handleSort = (col: 'name' | 'dealsInvolved' | 'avgScore' | 'highestRating' | 'lowestRating') => {
		const current = participantSearchQuery.sortBy ?? 'name'
		const currentOrder = participantSearchQuery.order ?? 'asc'
		const defaultDesc = col === 'avgScore' || col === 'highestRating' || col === 'lowestRating'
		const toggledOrder = currentOrder === 'asc' ? 'desc' : 'asc'
		const defaultOrder = defaultDesc ? 'desc' : 'asc'
		const nextOrder = current === col ? toggledOrder : defaultOrder
		setParticipantSearchQuery({ sortBy: col, order: nextOrder, page: 1 })
	}

	const sortIcon = (col: string) => {
		const active = (participantSearchQuery.sortBy ?? 'name') === col
		const ascending = (participantSearchQuery.order ?? 'asc') === 'asc'
		const directionIcon = ascending ? '▲' : '▼'
		const icon = active ? directionIcon : '⇅'
		return (
			<span className={`ml-1 inline-block text-[10px] ${active ? 'text-neutral-700' : 'text-neutral-300'}`}>
				{icon}
			</span>
		)
	}

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center gap-3'>
				<div className='flex-1'>
					<Search value={searchInput} onChange={(e) => handleSearch(e.target.value)} placeholder='Search' />
				</div>
				<ExportSplitButton items={participantsExportItems} />
			</div>

			<div className='overflow-x-auto rounded-lg border border-neutral-200'>
				<table className='w-full text-sm'>
					<thead className='bg-neutral-50'>
						<tr>
							<th className='w-10 px-6 py-3 text-left'>
								<Checkbox
									size='small'
									checked={allSelected || someSelected}
									isAllSelected={selectAllState}
									onChange={toggleAll}
								/>
							</th>
							<th
								className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer select-none hover:text-neutral-700'
								onClick={() => handleSort('name')}>
								Name{sortIcon('name')}
							</th>
							<th
								className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer select-none hover:text-neutral-700'
								onClick={() => handleSort('dealsInvolved')}>
								Deals Involved{sortIcon('dealsInvolved')}
							</th>
							<th
								className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer select-none hover:text-neutral-700'
								onClick={() => handleSort('avgScore')}>
								Avg Score{sortIcon('avgScore')}
							</th>
							<th className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap'>Highest Rated Deal</th>
							<th
								className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer select-none hover:text-neutral-700'
								onClick={() => handleSort('highestRating')}>
								Highest Rating{sortIcon('highestRating')}
							</th>
							<th
								className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap cursor-pointer select-none hover:text-neutral-700'
								onClick={() => handleSort('lowestRating')}>
								Lowest Rating{sortIcon('lowestRating')}
							</th>
							<th className='px-6 py-3 text-left text-xs text-neutral-500 whitespace-nowrap'>Actions</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-neutral-100'>
						{participants?.data.length === 0 && (
							<tr>
								<td colSpan={8} className='px-6 py-8 text-center text-neutral-500'>
									No participants found
								</td>
							</tr>
						)}
						{participants?.data.map((p) => (
							<tr key={p.userId} className={`hover:bg-neutral-50 ${selectedIds.has(p.userId) ? 'bg-blue-50/40' : ''}`}>
								<td className='px-6 py-4 text-left'>
									<Checkbox size='small' checked={selectedIds.has(p.userId)} onChange={() => toggleRow(p.userId)} />
								</td>
								<td className='px-6 py-4 text-left font-medium'>
									{[p.firstName, p.lastName].filter(Boolean).join(' ') || '\u2014'}
								</td>
								<td className='px-6 py-4 text-left text-neutral-500'>{formatDealsInvolved(p.cycleCount)}</td>
								<td className='px-6 py-4 text-left'>
									{p.avgScore != null ? (
										<span
											className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(Number(p.avgScore), scaleMax)}`}>
											{Number(p.avgScore).toFixed(2)}
										</span>
									) : (
										<span className='text-neutral-400'>{'\u2014'}</span>
									)}
								</td>
								<td className='px-6 py-4 text-left'>
									{p.highestRatedDealName && p.highestRatedDealScore != null ? (
										<span className='flex items-center gap-1.5'>
											<span
												className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(Number(p.highestRatedDealScore), scaleMax)}`}>
												{Number(p.highestRatedDealScore).toFixed(2)}
											</span>
											{p.highestRatedDealName}
										</span>
									) : (
										<span className='text-neutral-400'>{'\u2014'}</span>
									)}
								</td>
								<td className='px-6 py-4 text-left'>
									{p.highestRating != null ? (
										<span
											className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(Number(p.highestRating), scaleMax)}`}>
											{Number(p.highestRating).toFixed(2)}
										</span>
									) : (
										<span className='text-neutral-400'>{'\u2014'}</span>
									)}
								</td>
								<td className='px-6 py-4 text-left'>
									{p.lowestRating != null ? (
										<span
											className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getRatingColor(Number(p.lowestRating), scaleMax)}`}>
											{Number(p.lowestRating).toFixed(2)}
										</span>
									) : (
										<span className='text-neutral-400'>{'\u2014'}</span>
									)}
								</td>
								<td className='px-6 py-4 text-left'>
									<Button variant='secondary' size='small' onClick={() => selectParticipant(p.userId)}>
										View Profile
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{participants?.meta && (
				<Pagination meta={participants.meta} onPageChange={(p) => setParticipantSearchQuery({ page: p })} />
			)}
		</div>
	)
}

function Pagination({
	meta,
	onPageChange,
}: {
	meta: { page: number; perPage: number; total: number; totalInPage: number }
	onPageChange: (page: number) => void
}) {
	const totalPages = Math.ceil(meta.total / meta.perPage)
	const from = (meta.page - 1) * meta.perPage + 1
	const to = from + meta.totalInPage - 1

	const pages: number[] = []
	for (let i = 1; i <= totalPages && i <= 5; i++) {
		pages.push(i)
	}

	return (
		<div className='flex items-center justify-between rounded-b-lg border border-t-0 border-neutral-200 bg-white px-6 py-3 text-sm'>
			<span className='text-neutral-400'>
				Showing <span className='font-semibold text-neutral-800'>{from}</span> to{' '}
				<span className='font-semibold text-neutral-800'>{to}</span> of{' '}
				<span className='font-semibold text-neutral-800'>{meta.total}</span> results
			</span>
			<div className='flex items-center gap-3'>
				<button
					type='button'
					disabled={meta.page <= 1}
					className={`flex h-8 w-8 items-center justify-center rounded-sm text-sm ${meta.page <= 1 ? 'cursor-not-allowed text-neutral-300' : 'cursor-pointer text-neutral-600 hover:bg-neutral-50'}`}
					onClick={() => onPageChange(meta.page - 1)}>
					&lt;
				</button>
				{pages.map((p) => (
					<button
						key={p}
						type='button'
						className={`flex h-8 w-8 items-center justify-center rounded-sm text-sm font-medium ${
							p === meta.page ? 'bg-neutral-100 text-neutral-800' : 'text-neutral-400 hover:bg-neutral-50'
						}`}
						onClick={() => onPageChange(p)}>
						{p}
					</button>
				))}
				<button
					type='button'
					disabled={meta.page >= totalPages}
					className={`flex h-8 w-8 items-center justify-center rounded-sm text-sm ${meta.page >= totalPages ? 'cursor-not-allowed text-neutral-300' : 'cursor-pointer text-neutral-600 hover:bg-neutral-50'}`}
					onClick={() => onPageChange(meta.page + 1)}>
					&gt;
				</button>
			</div>
		</div>
	)
}

function formatOptionalDate(value: string | Date | null | undefined): string {
	if (!value) return '—'
	const d = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(d.getTime())) return '—'
	// Use UTC accessors so the calendar date matches the stored ISO date
	// regardless of the viewer's timezone. The Salesforce trigger writes
	// `crmEventDate` as `YYYY-MM-DDT00:00:00.000Z`, so a local-time format
	// would shift the day backwards for any viewer west of UTC.
	const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
	const dd = String(d.getUTCDate()).padStart(2, '0')
	const yyyy = d.getUTCFullYear()
	return `${mm}/${dd}/${yyyy}`
}
